/**
 * RWBR — a shop's mark, wherever a shop is named.
 *
 * `RaceLogo.tsx` for the other noun, and deliberately the same file twice over:
 * the box is sized from the `size` prop so a mark arriving late slides nothing,
 * and it renders an `<img>` and never an inline `<svg>`, because an SVG is a
 * document that can carry `<script>` and inlining one into this DOM would run
 * it. Both rules are written out in full in `RaceLogo.tsx`, `worker/logo.ts`
 * and `CarTopDown.tsx`; this is the fourth place they have to hold.
 *
 * MATCHED ON THE SHOP'S NAME, not its `teamId` — `tm-01` is a different shop in
 * every league, so a mark hung on it would follow a slot rather than a team.
 * `logoStore.ts` owns that decision and the whole argument for it.
 *
 * FALLS BACK TO THE PAINT, NOT TO A GREY BOX. Almost no shop has a mark until
 * Howard uploads one, and forty-three grey squares down a standings table would
 * be a worse screen than none. What stands in is the shop's own base colour —
 * a two-letter monogram on the ink the cars are painted in, which is a thing
 * the league already knows about itself.
 */

import { useState } from 'react';
import { logoForTeam, useLogos } from '../lib/logoStore.ts';

export interface TeamMarkProps {
  teamName: string | null | undefined;
  /** The shop's base ink, for the monogram when there is no mark. */
  color?: string | null;
  /** Box edge in px. The mark is fitted inside it, never stretched. */
  size?: number;
  className?: string;
}

/** "Hargrove Racing" → "HR"; "Vance" → "VA". Never more than two letters. */
export function monogram(name: string): string {
  const words = name
    .replace(/[^A-Za-z0-9 ]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return '—';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

export default function TeamMark({ teamName, color, size = 20, className }: TeamMarkProps) {
  const logo = logoForTeam(useLogos(), teamName);
  const [broken, setBroken] = useState(false);
  const name = teamName?.trim() ?? '';

  if (!name) return null;

  /**
   * AN INDEPENDENT IS NOT A SHOP, and a two-letter chip reading "IN" beside
   * forty names says the opposite. It keeps the BOX — the column has to line up
   * whether or not a man drives for somebody — and puts nothing in it.
   */
  if (name.toLowerCase() === 'independent') {
    return (
      <span
        className={`teammark teammark--none${className ? ` ${className}` : ''}`}
        style={{ width: size, height: size }}
        aria-hidden
        title="Independent — no shop"
      />
    );
  }

  if (!logo || broken) {
    return (
      <span
        className={`teammark teammark--ink${className ? ` ${className}` : ''}`}
        style={{
          width: size,
          height: size,
          background: color ?? 'var(--panel-2)',
          fontSize: Math.round(size * 0.44),
        }}
        aria-hidden
        title={name}
      >
        {monogram(name)}
      </span>
    );
  }

  return (
    <span
      className={`teammark${className ? ` ${className}` : ''}`}
      style={{ width: size, height: size }}
      // The shop is named in text beside this everywhere it is used, so the
      // mark is decoration and does not announce the team a second time.
      aria-hidden
    >
      <img
        src={logo.url}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        decoding="async"
        onError={() => setBroken(true)}
        title={logo.label}
      />
    </span>
  );
}
